import React, { useState, useEffect, useCallback } from 'react';
// import { useParams } from 'react-router-dom';
import axiosInstance from 'api/axiosInstance';
import InterfaceSelect from 'components/select';
import apiUrls from 'api/apiUrls';
import {
  Box,
  Heading,
  Button,
  useToast,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  // Input,
  // Select,
  Skeleton,
  useColorModeValue,
} from '@chakra-ui/react';
import Card from 'components/card/Card';
// import { useLocation } from 'react-router-dom';
import Default from 'components/fields/InputField';

const Interface = () => {
  const toast = useToast();
  // const navigate = useNavigate();
  // const { deviceUUID } = useParams();
  // const location = useLocation();
  // const { deviceId } = location.state || {};
  const deviceId = localStorage.getItem('deviceId');

  const [loading, setLoading] = useState(true);
  const [configurations, setConfigurations] = useState({});
  const [interfaceIdMap, setInterfaceIdMap] = useState({});

  //   const tableBg = useColorModeValue('white', 'navy.700');
  const tableBg = useColorModeValue('white', 'navy.700');
  const inputBg = useColorModeValue('white', 'navy.600');
  const inputColor = useColorModeValue('navy.800', 'white');
  const inputBorder = useColorModeValue('gray.300', 'navy.600');
  //   const skeletonBg = useColorModeValue('navy.50', 'navy.100');

  const methodOptions = { RTU: 'rtu', ASCII: 'ascii', TCP: 'tcp' };

  const parityOptions = { None: 'N', Odd: 'O', Even: 'E' };

  const baudRateOptions = {
    300: 300,
    1200: 1200,
    2400: 2400,
    4800: 4800,
    9600: 9600,
    19200: 19200,
    38400: 38400,
    57600: 57600,
    115200: 115200,
    230400: 230400,
    460800: 460800,
  };

  const stopBitOptions = { 0: 0, 1: 1, 2: 2 };

  const byteSizeOptions = { 7: 7, 8: 8 };

  const optionsMap = {
    method: methodOptions,
    parity: parityOptions,
  };

  const defaultConfigs = {
    default: {
      method: 'rtu',
      port: '/dev/ttyO1',
      baudrate: 4800,
      timeout: 1000,
      parity: 'O',
      stopbit: 1,
      bytesize: 7,
      pollperiod: 5000,
      host: '',
      interface: 0,
    },
  };

  const fetchConfigurations = useCallback(async () => {
    try {
      const keys = 'interfaces';
      const resp = await axiosInstance.get(
        apiUrls.groupAttributes(deviceId, keys),
      );
      const attrMap = Object.fromEntries(
        resp.data.map((a) => [a.key, a.value]),
      );

      const mergedConfigs = attrMap.interfaces.reduce((acc, i) => {
        const settings = i.settings || {};
        const name = i.id || '';
        // Normalize method to lowercase for select compatibility
        if (settings.method) {
          settings.method = settings.method.toLowerCase();
        }
        acc[name] = settings;
        return acc;
      }, {});

      const interfaceIdMap = attrMap.interfaces.reduce((acc, i) => {
        const id = i.id || '';
        const name = i.name || '';
        acc[id] = name;
        return acc;
      }, {});

      setInterfaceIdMap(interfaceIdMap);
      setConfigurations(mergedConfigs);
    } catch (err) {
      console.error('Failed to fetch configurations', err);
    } finally {
      setLoading(false);
    }
  }, [deviceId]);

  useEffect(() => {
    fetchConfigurations();
  }, [fetchConfigurations]);

  const handleInputChange = (iface, key, value) => {
    setConfigurations((prev) => ({
      ...prev,
      [iface]: {
        ...prev[iface],
        [key]: value,
      },
    }));
  };

  const handleSave = async (iface) => {
    const integerFields = [
      'baudrate',
      'timeout',
      'stopbit',
      'bytesize',
      'pollperiod',
    ];
    const config = { ...configurations[iface] };
    integerFields.forEach((key) => {
      if (config.hasOwnProperty(key)) {
        config[key] = parseInt(config[key], 10);
      }
    });

    const payload = {
      settings: config,
    };

    try {
      await axiosInstance.post(
        apiUrls.saveDeviceGroupInterface(deviceId, iface),
        payload,
      );

      toast({
        title: 'Saved',
        description: `${interfaceIdMap[iface]} configuration updated`,
        status: 'success',
        duration: 3000,
        position: 'top',
        isClosable: true,
      });
    } catch (err) {
      console.error(err);
      toast({
        title: 'Error',
        description: `Failed to save ${interfaceIdMap[iface]}`,
        status: 'error',
        position: 'top',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  if (loading) {
    return (
      <Box>
        <Heading size="lg" mb={6}>
          Interface Configurations
        </Heading>

        <Card px={0} py={6}>
          <Box overflowX="auto" maxWidth="100%">
            <Table size="md" width="100%" mt={2}>
              <Thead>
                <Tr>
                  <Th>Interface</Th>
                  {Object.keys(defaultConfigs['default']).map((key) => (
                    <Th key={key}>{key}</Th>
                  ))}
                  <Th>Action</Th>
                </Tr>
              </Thead>
              <Tbody>
                {[...Array(4)].map((_, rowIndex) => (
                  <Tr key={rowIndex}>
                    <Td>
                      <Skeleton height="20px" width="100px" />
                    </Td>
                    {Object.keys(defaultConfigs['default']).map(
                      (key, colIndex) => (
                        <Td key={colIndex}>
                          <Skeleton height="20px" />
                        </Td>
                      ),
                    )}
                    <Td>
                      <Skeleton height="32px" width="60px" borderRadius="md" />
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        </Card>
      </Box>
    );
  }

  const configKeys = Object.keys(Object.values(defaultConfigs)[0]);
  // console.log('configurations', JSON.stringify(configurations));

  return (
    <Box>
      <Heading size="lg" mb={6}>
        Interface Configurations
      </Heading>

      <Card px={0} py={6}>
        <Box overflowX="auto" maxWidth="100%">
          <Table size="md" width="100%" mt={2}>
            <Thead>
              <Tr>
                <Th
                  position="sticky"
                  left="0"
                  zIndex="3"
                  boxShadow="md"
                  bg={tableBg}
                  borderColor="transparent"
                >
                  Interface
                </Th>
                {configKeys.map((key) => (
                  <Th borderColor="transparent" key={key}>
                    {key === 'pollperiod' || key === 'timeout'
                      ? `${key} (sec)`
                      : key}
                  </Th>
                ))}
                <Th borderColor="transparent">Action</Th>
              </Tr>
            </Thead>
            <Tbody>
              {Object.entries(configurations).map(([iface, config]) => (
                <Tr key={iface}>
                  <Td
                    borderColor="transparent"
                    minWidth="120px"
                    fontWeight="bold"
                    whiteSpace="nowrap"
                    position="sticky"
                    left="0"
                    zIndex="2"
                    boxShadow="md"
                    fontSize="sm"
                    bg={tableBg}
                    pointerEvents="auto"
                    // ...existing props...
                  >
                    {interfaceIdMap[iface]}
                  </Td>
                  {configKeys.map((key) => (
                    <Td
                      borderColor="transparent"
                      key={key}
                      px={1}
                      py={4}
                      minWidth="120px"
                    >
                      {console.log(
                        'iface:',
                        iface,
                        'name:',
                        interfaceIdMap[iface],
                      )}

                      {key === 'baudrate' &&
                      interfaceIdMap[iface] === 'Modbus_TCP' ? (
                        <Box textAlign="center" width="100%">
                          -
                        </Box>
                      ) : key === 'method' ||
                        key === 'parity' ||
                        key === 'stopbit' ||
                        key === 'bytesize' ||
                        key === 'baudrate' ? (
                        <InterfaceSelect
                          value={config[key]}
                          onChange={(value) =>
                            handleInputChange(iface, key, value)
                          }
                          options={
                            key === 'baudrate'
                              ? baudRateOptions
                              : key === 'stopbit'
                                ? stopBitOptions
                                : key === 'bytesize'
                                  ? byteSizeOptions
                                  : optionsMap[key]
                          }
                          isDisabled={key === 'interface'}
                        />
                      ) : (
                        <Default
                          value={config[key]}
                          onChange={(e) =>
                            handleInputChange(iface, key, e.target.value)
                          }
                          size="sm"
                          borderRadius="md"
                          paddingX={2}
                          paddingY={1}
                          fontSize="sm"
                          bg={inputBg}
                          border="1px solid"
                          color={inputColor}
                          borderColor={inputBorder}
                          isDisabled={key === 'interface' || key === 'port'}
                        />
                      )}
                    </Td>
                  ))}
                  <Td borderColor="transparent">
                    <Button
                      variant="outline"
                      border="2px"
                      colorScheme="green"
                      size="sm"
                      onClick={() => handleSave(iface)}
                      isDisabled={iface === 'interfaces'}
                    >
                      Save
                    </Button>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      </Card>
    </Box>
  );
};

export default Interface;
